import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  InputLabel,
  FormControl,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Paper,
  Avatar,
  FormHelperText
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { firestore, auth } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import logo from '../cosmologo.png';
import { v4 as uuidv4 } from 'uuid';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#013a64',
    },
    secondary: {
      main: '#4CAF50',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
    },
  },
});

const ExitRequestScreen = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState(new Date());
  const [purpose, setPurpose] = useState('');
  const [exitType, setExitType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uniqueCode, setUniqueCode] = useState('');
  const [errors, setErrors] = useState({
    date: false,
    time: false,
    purpose: false,
    exitType: false,
  });

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleSubmit = async () => {
    const newErrors = {
      date: !date,
      time: !time,
      purpose: !purpose.trim(),
      exitType: !exitType,
    };
    
    setErrors(newErrors);
    
    if (Object.values(newErrors).some(error => error)) {
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      alert('Please log in to submit a request.');
      return;
    }

    const generatedCode = uuidv4();
    setUniqueCode(generatedCode);

    setIsLoading(true);
    try {
      const requestRef = collection(firestore, 'students', user.uid, 'requests');
      await addDoc(requestRef, {
        date: formatDate(date),
        time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        purpose,
        exitType,
        status: 'Pending',
        createdAt: new Date(),
        requestId: generatedCode,
      });
      alert('Request Submitted. Your request is awaiting approval.');
      navigate('/RequestHistory');
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Header */}
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <IconButton 
              edge="start" 
              color="inherit" 
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Back
            </Typography>
          </Toolbar>
        </AppBar>

        <Container 
          maxWidth="sm" 
          sx={{ 
            py: 4,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              width: '100%',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Logo and Title */}
            <Avatar
              src={logo}
              alt="Cosmo Logo"
              sx={{ 
                width: 100, 
                height: 100, 
                mb: 3,
                boxShadow: 2
              }}
            />
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
              Student Pass Request Form
            </Typography>

            {/* Form Fields */}
            <Box sx={{ width: '100%', mb: 3 }}>
              {/* Date Picker */}
              <FormControl fullWidth sx={{ mb: 3 }} error={errors.date}>
                <DatePicker
                  selected={date}
                  onChange={(date) => {
                    setDate(date);
                    setErrors({...errors, date: false});
                  }}
                  className="date-picker"
                  dateFormat="MMMM d, yyyy"
                  customInput={
                    <TextField 
                      fullWidth 
                      variant="outlined" 
                      label="Date"
                      error={errors.date}
                      helperText={errors.date ? "Date is required" : ""}
                    />
                  }
                  disabled
                />
              </FormControl>

              {/* Time Picker */}
              <FormControl fullWidth sx={{ mb: 3 }} error={errors.time}>
                <DatePicker
                  selected={time}
                  onChange={(time) => {
                    setTime(time);
                    setErrors({...errors, time: false});
                  }}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  customInput={
                    <TextField 
                      fullWidth 
                      variant="outlined" 
                      label="Time"
                      error={errors.time}
                      helperText={errors.time ? "Time is required" : ""}
                    />
                  }
                />
              </FormControl>

              {/* Exit Type Select */}
              <FormControl fullWidth sx={{ mb: 3 }} error={errors.exitType}>
                <InputLabel>Type of Exit</InputLabel>
                <Select 
                  value={exitType} 
                  onChange={(e) => {
                    setExitType(e.target.value);
                    setErrors({...errors, exitType: false});
                  }}
                  label="Type of Exit"
                  error={errors.exitType}
                >
                  <MenuItem value="">
                    <em>Select Type</em>
                  </MenuItem>
                  <MenuItem value="Returning back to campus today">Returning back to campus today</MenuItem>
                  <MenuItem value="Not returning back to campus today">Not returning back to campus today</MenuItem>
                </Select>
                {errors.exitType && <FormHelperText>Exit type is required</FormHelperText>}
              </FormControl>

              {/* Purpose TextField */}
              <TextField
                label="Purpose of Exit"
                multiline
                rows={4}
                fullWidth
                value={purpose}
                onChange={(e) => {
                  setPurpose(e.target.value);
                  setErrors({...errors, purpose: false});
                }}
                variant="outlined"
                sx={{ mb: 3 }}
                error={errors.purpose}
                helperText={errors.purpose ? "Purpose is required" : ""}
              />
            </Box>

            {/* Submit Button */}
            <Button 
              variant="contained" 
              fullWidth 
              onClick={handleSubmit} 
              disabled={isLoading}
              size="large"
              sx={{
                py: 1.5,
                borderRadius: 1,
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: 'common.white' }} />
              ) : (
                'Submit Request'
              )}
            </Button>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ExitRequestScreen;